import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useTranslationVersion } from '@lib/i18n/useTranslationVersion';
import { usePortalJobsStream } from '@features/portalJobs';
import { useNotificationsStream } from '@features/notifications';
import { useSubscription } from '@features/billing/hooks/useSubscription';
import SubscriptionLocked from '@features/billing/components/SubscriptionLocked';

export function AppShell() {
  useTranslationVersion();
  // Subscribe once for the entire authenticated app — invalidates portal-job
  // caches as updates arrive so every page that watches a job stays fresh
  // without opening its own SSE connection (Stream 4.C1).
  usePortalJobsStream();
  // Same pattern for push notifications — one SSE connection per tab pushes
  // toasts, refreshes the bell badge, and invalidates the notification list.
  useNotificationsStream();
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  // Subscription expiry gate: an EXPIRED tenant can only reach the subscription
  // page. `blocked` is fail-open (never true while loading / on error), so a
  // transient billing-API hiccup can't lock a paying tenant out. All hooks above
  // run unconditionally before this early return (Rules of Hooks).
  const { blocked, data: sub } = useSubscription();
  if (blocked) return <SubscriptionLocked />;

  const expiringDays = sub?.status === 'expiring' ? sub.daysRemaining : null;

  return (
    // App shell with internal scroll: the shell itself is exactly viewport
    // height (`h-screen`) and `overflow-hidden`, so the page never scrolls
    // the body. Only the `<main>` panel scrolls (`overflow-y-auto`), which
    // means the Sidebar and Header are physically anchored to the viewport
    // — they never move with the content. This is the professional desktop
    // pattern (Slack/Notion/Gmail-style) and works in every browser without
    // relying on `position: sticky` to do all the heavy lifting.
    <div className="flex h-screen overflow-hidden">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:start-2 focus:z-[100] focus:rounded-md focus:bg-brand focus:px-4 focus:py-2 focus:text-sm focus:text-white focus:shadow-elevated"
      >
        {isRtl ? 'تخطي إلى المحتوى الرئيسي' : 'Skip to main content'}
      </a>
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-y-auto p-4 md:p-6 animate-fade-in outline-none"
        >
          {expiringDays != null && (
            <a
              href="/app/billing"
              className="mb-4 flex items-center gap-2 rounded-md border border-warning/40 bg-warning-muted/30 px-4 py-2.5 text-sm text-warning hover:bg-warning-muted/50 transition-colors"
            >
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="flex-1">
                {isRtl
                  ? `ينتهي اشتراكك خلال ${expiringDays} ${expiringDays === 1 ? 'يوم' : 'يوماً'}. جدّد الآن لتجنّب انقطاع الخدمة.`
                  : `Your subscription expires in ${expiringDays} day${expiringDays === 1 ? '' : 's'}. Renew now to avoid interruption.`}
              </span>
              <span className="font-semibold underline shrink-0">{isRtl ? 'تجديد' : 'Renew'}</span>
            </a>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
